import { cx } from '@parrot/utils';
import styles from './SegmentedControl.module.css';

export interface SegmentedOption {
  value: string;
  label: string;
  count?: number;
}

export interface SegmentedControlProps {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /**
   * 'pill' (default) — the gold-filled pill. 'field' — sits in a filter bar:
   * same 36px box as a Select, the chosen segment raised in neutral, and gold
   * only once the value differs from `defaultValue` (gold = "you changed this").
   */
  variant?: 'pill' | 'field';
  defaultValue?: string;
  /**
   * When true the whole control is greyed out and inert (buttons get the native
   * `disabled` attribute, clicks are no-ops). Used when a custom portfolio scope
   * is active and content-type scoping is mutually exclusive with it.
   */
  disabled?: boolean;
}

/** Lifted from location-intelligence: a gold-active segmented pill with count badges. */
export function SegmentedControl({
  options,
  value,
  onChange,
  className,
  variant = 'pill',
  defaultValue,
  disabled = false,
}: SegmentedControlProps) {
  const field = variant === 'field';
  const isSet = field && defaultValue !== undefined && value !== defaultValue;
  return (
    <div
      className={cx(
        field ? styles.field : styles.bar,
        isSet && styles.fieldSet,
        disabled && styles.disabled,
        className,
      )}
      role={'radiogroup'}
      aria-disabled={disabled || undefined}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type={'button'}
          role={'radio'}
          aria-checked={opt.value === value}
          disabled={disabled}
          className={cx(
            field ? styles.fieldOption : styles.option,
            opt.value === value && (field ? styles.fieldOptionActive : styles.optionActive),
          )}
          onClick={disabled ? undefined : () => onChange(opt.value)}
        >
          {opt.label}
          {opt.count !== undefined && <span className={styles.count}>{opt.count}</span>}
        </button>
      ))}
    </div>
  );
}
